import React, { ReactNode, useMemo } from 'react';
import InfoFish from '../../../../../pages/PlanOperationPage/Popup/EditOp/_components/InfoFish';
import { InfoMode, OP_STATUS } from '../../../../../util/varibles/constants';
import { firstLetter, formatNumber } from '../../../../../util/varibles/global';
import { IDiseaseOfSite, IHarvest2, IUnit, IUnitSummary } from "../../../../../util/varibles/interface";
import Tabs from "../../../../Basic/Tabs";
import Icon, { ICON_NAME } from "../../../../Icon";
import UnitDisease from "../../../../Operation/FishDisease/UnitDisease";
import UnitLice from "../../../../Operation/UnitLice";
import FishHealthInfo from "../../_component/FishHealthInfo";
import LineInfo from "../../_component/LineInfo";
import styles from './style.module.scss';

type GroupByTank = {
    [tankId: string]: {
        name?: string;
        total_weight: number;
        fish_amount: number;
        methods: {
            [id: string]: {
                name?: string;
                total_weight: number;
                fish_amount: number;
            };
        };
    };
};

export const PlanInfo: React.FC<{ data: IUnit, children?: ReactNode, showPlan?: boolean }> = ({ data, children, showPlan = true }) => {
    const { delivery_types = [] } = data;
    const grouped = delivery_types.reduce<GroupByTank>((rs, item) => {
        item.tanks.forEach((tank) => {
            if (!rs[tank.id]) {
                rs[tank.id] = {
                    name: tank.name,
                    total_weight: 0,
                    fish_amount: 0,
                    methods: {},
                };
            }
            const { fish_amount = 0, total_weight = 0 } = tank;
            rs[tank.id].fish_amount += fish_amount;
            rs[tank.id].total_weight += total_weight;

            if (!rs[tank.id].methods[item.id]) {
                rs[tank.id].methods[item.id] = {
                    name: item.name,
                    total_weight: 0,
                    fish_amount: 0,
                };
            }

            rs[tank.id].methods[item.id].fish_amount += fish_amount;
            rs[tank.id].methods[item.id].total_weight += total_weight;
        });

        return rs;
    }, {});

    return <div className={styles.table}>
        <div className={styles.line} data-row-lev="header">
            <div>Amount</div>
            <div>Avg. weight</div>
            <div>Total weight</div>
            <div className='grid grid-cols-2'>
                <div>Loaded tanks</div>
                <div>Factory discharge</div>
            </div>
        </div>
        {showPlan && <div className={styles.line}>
            <div className='text-[15px]'>{formatNumber(data.fish_amount)}</div>
            <div className='text-[15px]'>{formatNumber(Math.round(data.avg_weight))} gr</div>
            <div className='text-[15px]'>{formatNumber(Math.round(data.total_weight / 10000) / 100)} tn</div>
            <div className='!text-[15px] flex flex-col !p-0'>
                {Object.keys(grouped).map(key => {
                    const { name, fish_amount, total_weight, methods } = grouped[key];
                    return <div key={key} className='grid grid-cols-2 py-[2px] [&:not(:last-child)]:border-b border-[var(--border1)] flex-wrap'>
                        <div className='flex gap-[5px]'>
                            <div className='text-comment whitespace-nowrap'>{name}:</div>
                            <InfoFish fontSize={15} data={{ fish_amount, total_weight }} />
                        </div>
                        <div className='flex flex-col'>
                            {Object.keys(methods).map(sub => {
                                const { name: subName, fish_amount: subAmount, total_weight: subTotal } = methods[sub];
                                return <div key={sub} className='flex gap-[5px]'>
                                    <div className='text-comment  whitespace-nowrap'>{subName}:</div>
                                    <InfoFish fontSize={15} data={{ fish_amount: subAmount, total_weight: subTotal }} />
                                </div>
                            })}
                        </div>
                    </div>
                })}
            </div>
        </div>}
        {children}
    </div>
}

const UnitInfo: React.FC<{
    data?: IUnit,
    diseases?: { [id: string]: IDiseaseOfSite },
    actual?: { [unit_id: string]: IUnitSummary },
    infoMode: InfoMode,
    operationStatus?: OP_STATUS
}> = ({ data, diseases = {}, actual = {}, infoMode, operationStatus }) => {
    if (!data)
        return null;

    const { unit_id } = data;
    const actualInfo = actual[unit_id];
    const { tanks = [], fish_amount = 0, total_weight = 0 } = actualInfo || {};
    return <>
        <FishHealthInfo value={data.fish_health} />
        <LineInfo label="Unit diseases" className='h-[26px]' icon>
            <>
                <Icon icon={ICON_NAME.DISEASE} className='mt-[2px]' />
                <UnitDisease unit={data} />
            </>
        </LineInfo>

        <LineInfo label="Lice per fish" icon className="h-[26px] pl-1">
            <>
                <Icon icon={ICON_NAME.LICE} className='mt-[2px]' />
                <UnitLice lice={data.lice || (diseases[data.site_id || ''] || {}).lice} />
            </>
        </LineInfo>
        <LineInfo label="Mortality risk" className='h-[26px]' icon>
            <>{data.mortality_risk ? 'High' : 'Low'}</>
        </LineInfo>
        <LineInfo label="Fish group ID" className='h-[26px]' icon>
            <>{data.fish_group || "-"}</>
        </LineInfo>
        <PlanInfo data={data} showPlan={infoMode === InfoMode.PLAN}>
            {(infoMode === InfoMode.ACTUAL && actualInfo && !!total_weight) && <div className={styles.line} >
                <div className='text-[15px]'>{formatNumber(fish_amount)}</div>
                <div className='text-[15px]'>{formatNumber(Math.round((total_weight || 1) / (fish_amount || 1)))} gr</div>
                <div className='text-[15px]'>{formatNumber(Math.round(total_weight / 10000) / 100)} tn</div>
                <div className='!text-[15px] flex flex-col !p-0'>
                    {tanks.map(item => {
                        const { tank_id, name, load, discharge } = item;
                        const tankInfo = load.reduce((rs: { total_weight: number, fish_amount: number }, item) => {
                            rs.total_weight += item.total_weight;
                            rs.fish_amount += item.fish_amount;
                            return rs;
                        }, { total_weight: 0, fish_amount: 0 })
                        return <div key={tank_id} className='grid grid-cols-2 py-[2px] [&:not(:last-child)]:border-b border-[var(--border1)]'>
                            <div className='flex gap-[5px]'>
                                <div className='text-comment'>{name}:</div>
                                <InfoFish fontSize={15} data={tankInfo} />
                            </div>
                            <div className='flex flex-col'>
                                {discharge.map(sub => {
                                    const { delivery_type_id, name: subName, fish_amount: subAmount, total_weight: subTotal } = sub;
                                    return <div key={delivery_type_id} className='flex gap-[5px]'>
                                        <div className='text-comment'>{firstLetter(subName.replace(/_/g, ' '))}:</div>
                                        <InfoFish fontSize={15} data={{ fish_amount: subAmount, total_weight: subTotal }} />
                                    </div>
                                })}
                            </div>
                        </div>
                    })}
                </div>
            </div>}
            {(infoMode === InfoMode.ACTUAL && (!actualInfo || !total_weight)) && <div className={styles.line}>
                <div className='col-span-4 text-center text-[13px] font-[600] text-comment'>No data</div>
            </div>}
        </PlanInfo>
    </>
}

interface IProps {
    operation: IHarvest2
    infoMode: InfoMode
}

const HarvestInfo: React.FC<IProps> = ({ operation, infoMode }) => {
    const { sub_operations, diseases = [], summary = [] } = operation;
    const diseaseObjects = useMemo(() => diseases.reduce((rs: { [id: string]: IDiseaseOfSite }, item) => {
        rs[item.id] = item;
        return rs;
    }, {}), [diseases]);

    const actual = useMemo(() => {
        if (infoMode !== InfoMode.ACTUAL)
            return Object.fromEntries([]);
        return summary.reduce((rs: { [unit_id: string]: IUnitSummary }, item) => {
            rs[item.unit_id] = item.actual;
            return rs;
        }, {})
    }, [infoMode, summary])

    const tabs = useMemo(() => sub_operations.map(item => ({
        name: `${item.site_name} - ${item.unit_id} ${(item.harvest_id && item.harvest_id !== '-') ? `(Harvest ID: ${item.harvest_id})` : ''}`,
        data: item
    })), [sub_operations]);

    return <Tabs opts={tabs}>
        <UnitInfo diseases={diseaseObjects} actual={actual} infoMode={infoMode}
            operationStatus={operation.status} />
    </Tabs>;
};

export default HarvestInfo;
